import React from "react";
import { useTranslation } from "react-i18next";

import { Identifiable, ProjectResource } from "@planzone/dto";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { UseCases } from "@planzone/core";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { Safeguard } from "@scenes/safeguard";
import { useModal } from "@root/client/modal";
import { Resource } from "../resource";
import { sortResourceList } from "../sort";
import { SkeletonDisplay } from "./skeletonDisplay";
import { ProjectResourceSelect } from "./projectResourceSelect";
import { Placeholder } from "./placeholder";
import { Props } from "./types";
import styles from "./styles.scss";
import { CogWheelIcon } from "@common/cogWheelIcon";

function GroupsList(props: Props) {
  const { t, i18n } = useTranslation();
  const { pushView } = useModal();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "resource",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    props.project.uuid,
    "resource",
  );
  const template = [...planzoneTemplate, ...projectTemplate];
  const currentLang =
    ["fr", "en"].find((lang) => new RegExp(lang, "i").test(i18n.language)) ||
    "fr";
  const isFiltered = ({ resource, role }: Identifiable<ProjectResource>) =>
    (props.filter.resourceTypes.length === 0 ||
      props.filter.resourceTypes.includes(resource.type)) &&
    (props.filter.roles.length === 0 ||
      props.filter.roles.find(({ uuid }) => role.uuid === uuid)) &&
    (!props.filter.resourceTags ||
      props.filter.resourceTags.length === 0 ||
      resource.tags.some(
        (tag) =>
          props.filter.resourceTags &&
          !!props.filter.resourceTags.find((t) => t.name === tag.name),
      )) &&
    (!props.filter.inactiveOnly || resource.deactivated) &&
    (!props.filter.activeOnly || !resource.deactivated);

  return props.isRequestPending ? (
    <div className={styles.groupsList}>
      <SkeletonDisplay />
      <SkeletonDisplay />
    </div>
  ) : props.groups.length === 0 ? (
    <Placeholder project={props.project} />
  ) : (
    <div className={styles.groupsList}>
      {props.groups.map((group) => (
        <div key={group.uuid} className={styles.group}>
          <div className={styles.resourcesList}>
            <div className={styles.heading}>
              <p className={styles.label}>{t("membersList.name")}</p>
              {props.columns.map(({ key }) => {
                const criteria = template.find(({ uuid }) => uuid === key);
                const label = criteria
                  ? criteria.labels[
                      currentLang as keyof typeof criteria.labels
                    ] || criteria.name
                  : t(
                      `projectMembersListingConfiguration.columnNames.${key}`,
                      key,
                    );

                return (
                  <p key={key} className={styles.label}>
                    {label}
                  </p>
                );
              })}
              <Safeguard
                useCases={[UseCases.editProjectMembersListingConfiguration]}
              >
                {({ canEditProjectMembersListingConfiguration }) => (
                  <div className={`${styles.label} ${styles.cogWheel}`}>
                    {canEditProjectMembersListingConfiguration && (
                      <div
                        onClick={() =>
                          pushView({
                            key: UseCases.retrieveProjectMembersListingConfiguration,
                            data: { project: props.project },
                          })
                        }
                      >
                        <CogWheelIcon className={styles.cogIcon} />
                      </div>
                    )}
                  </div>
                )}
              </Safeguard>
            </div>
            <ul className={styles.list}>
              {sortResourceList(group.resources, props.sort)
                .filter(isFiltered)
                .map((projectResource) => (
                  <li key={projectResource.uuid} className={styles.item}>
                    <Resource
                      projectResource={projectResource}
                      columns={props.columns}
                    />
                  </li>
                ))}
            </ul>
          </div>
          <div className={styles.groupHeader}>
            <div>
              <h2 className={styles.name}>{group.name}</h2>
              <p className={styles.members}>
                {group.resources.length} {t("membersList.groupLength")}
              </p>
            </div>
            <div className={styles.actions}>
              <Safeguard useCases={[UseCases.editResourceGroup]}>
                {({ canEditResourceGroup }) =>
                  canEditResourceGroup && (
                    <div>
                      <ProjectResourceSelect
                        project={props.project}
                        projectResources={
                          group.resources as Identifiable<ProjectResource>[]
                        }
                        group={group}
                      />
                    </div>
                  )
                }
              </Safeguard>
              <div className={styles.menu}>
                <SecureContextualMenu
                  items={[
                    {
                      name: t("membersList.contextualMenu.edit"),
                      useCase: UseCases.editResourceGroup,
                      act: () =>
                        pushView({
                          key: UseCases.editResourceGroup,
                          data: { group, namespace: props.project.uuid },
                        }),
                    },
                    {
                      name: t("membersList.contextualMenu.delete"),
                      color: "#DB5F63FF",
                      useCase: UseCases.removeResourceGroup,
                      act: () =>
                        pushView({
                          key: UseCases.removeResourceGroup,
                          data: group,
                        }),
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export { GroupsList };
